// Settings → Security → Sign-ins: the company's history (DESIGN-security.md, ideas 3, 4 and 6).
// Admins only. The lines' words are the server's (`services/security_log.describe`).
import React, { useCallback, useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { securityApi } from '../../lib/api';
import { addDays, dayDate, todayFrom } from '../../lib/securityWords';
import HistoryLines from './HistoryLines';
import SecurityCredit from './SecurityCredit';

const WHAT = [
  ['', 'Everything'],
  ['signins', 'Sign-ins'],
  ['problems', 'Wrong passwords and lockouts'],
  ['changes', 'Password and security changes'],
  ['signouts', 'Sign-outs'],
  ['reminders', 'Reminders and invitations'],
];
const WHEN = [[7, 'Last 7 days'], [30, 'Last 30 days'], [90, 'Last 90 days'], [365, 'Last 12 months'],
  // "A particular day…" (Rob, 25/10/2026): "we had a problem this day, let's see what was going on".
  ['day', 'A particular day…']];
/** How far back a day can be chosen — the history is kept 12 months. */
const KEEP_DAYS = 365;

const selectCls = 'border border-border rounded-md px-2.5 py-1.5 text-sm bg-background';

export default function SignInHistory() {
  // `on` is the chosen day ("YYYY-MM-DD", the company's clock) when `days` is 'day'.
  const [filters, setFilters] = useState({ person: '', what: '', days: 30, on: null });
  // Today on the company's clock, from the server — never the laptop's date.
  const [today, setToday] = useState(null);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [more, setMore] = useState(false);
  const [exporting, setExporting] = useState(false);

  const params = useCallback((extra = {}) => {
    const p = filters.days === 'day' && filters.on ? { on: filters.on, ...extra } : { days: filters.days === 'day' ? 30 : filters.days, ...extra };
    if (filters.person) p.person = filters.person;
    if (filters.what) p.what = filters.what;
    return p;
  }, [filters]);

  useEffect(() => {
    // A particular day waits until one is chosen (it starts on today, once we know today).
    if (filters.days === 'day' && !filters.on) return;
    setData(null);
    securityApi.history(params())
      .then((r) => { setData(r.data); setFailed(false); if (r.data.today) setToday(r.data.today); })
      .catch((e) => {
        if (e.response?.status === 400 && filters.days === 'day') {
          toast.error(e.response.data?.detail || 'Choose a day in the last 12 months.');
          setFilters((f) => (today ? { ...f, on: today } : { ...f, days: 30, on: null }));
        } else setFailed(true);
      });
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const older = async () => {
    setMore(true);
    try {
      const r = await securityApi.history(params({ before: data.oldest }));
      // A day split across two pages is joined back into one heading.
      const days = [...data.days];
      for (const d of r.data.days) {
        const last = days[days.length - 1];
        if (last && last.date === d.date) last.lines = [...last.lines, ...d.lines];
        else days.push(d);
      }
      setData({ ...r.data, days });
    } catch {
      toast.error('Could not load older sign-ins');
    } finally {
      setMore(false);
    }
  };

  const download = async (format) => {
    setExporting(true);
    try {
      const r = await securityApi.exportHistory(params({ format }));
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filters.on && filters.days === 'day'
        ? `sign-in-history_${filters.on}.${format}` : `sign-in-history.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download the history');
    } finally {
      setExporting(false);
    }
  };

  if (failed) {
    return <div className="card-hive p-6"><p className="text-sm text-muted-foreground">Couldn't load the sign-in history. Please refresh the page.</p></div>;
  }
  const firstLine = data && data.days[0] && data.days[0].lines[0];
  const todayIs = today || (data && data.today) || todayFrom(firstLine ? firstLine.at : null);
  const oneDay = filters.days === 'day';
  const earliest = todayIs ? addDays(todayIs, -KEEP_DAYS) : undefined;
  const step = (n) => setFilters((f) => {
    const next = addDays(f.on || todayIs, n);
    if (next > todayIs || (earliest && next < earliest)) return f;
    return { ...f, on: next };
  });

  return (
    <div className="card-hive p-6" data-testid="security-history">
      <h3 className="font-semibold">Sign-in history</h3>
      <div className="flex flex-wrap gap-2 mt-3 items-center">
        <select className={selectCls} value={filters.person} aria-label="Person"
                onChange={(e) => setFilters((f) => ({ ...f, person: e.target.value }))}>
          <option value="">Everyone</option>
          {(data?.people || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select className={selectCls} value={filters.what} aria-label="What"
                onChange={(e) => setFilters((f) => ({ ...f, what: e.target.value }))}>
          {WHAT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select className={selectCls} value={filters.days} aria-label="When" data-testid="history-when"
                onChange={(e) => {
                  const v = e.target.value;
                  setFilters((f) => (v === 'day'
                    ? { ...f, days: 'day', on: f.on || todayIs }
                    : { ...f, days: Number(v) }));
                }}>
          {WHEN.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {oneDay && (
          <div className="flex items-center gap-1" data-testid="history-day-picker">
            <Button variant="outline" size="sm" className="h-8 px-2" aria-label="The day before"
                    disabled={!filters.on || filters.on <= earliest} onClick={() => step(-1)}>←</Button>
            <input type="date" className={selectCls} aria-label="Day" data-testid="history-day"
                   value={filters.on || ''} min={earliest} max={todayIs || undefined}
                   onChange={(e) => e.target.value && setFilters((f) => ({ ...f, on: e.target.value }))} />
            <Button variant="outline" size="sm" className="h-8 px-2" aria-label="The day after"
                    disabled={!filters.on || filters.on >= todayIs} onClick={() => step(1)}>→</Button>
          </div>
        )}
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" disabled={exporting} onClick={() => download('xlsx')}>
            <Download className="w-3.5 h-3.5 mr-1" />Excel
          </Button>
          <Button variant="outline" size="sm" disabled={exporting} onClick={() => download('csv')}>CSV</Button>
        </div>
      </div>

      {!data ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <>
          {oneDay && filters.on && data.days.length === 0 && (
            <p className="text-sm text-muted-foreground py-4" data-testid="history-day-empty">
              Nothing on {dayDate(filters.on)}{filters.person || filters.what ? ' for these filters' : ''}.
            </p>
          )}
          {!(oneDay && data.days.length === 0) && <HistoryLines days={data.days} today={todayIs} showIp />}
          {data.more && (
            <div className="flex justify-center mt-3">
              <Button variant="outline" size="sm" onClick={older} disabled={more}>
                {more && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Show older
              </Button>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-4">
            Kept for 12 months, then deleted. Location is the country only, worked out on our own server.
          </p>
          <SecurityCredit credit={data.credit} />
        </>
      )}
    </div>
  );
}
